import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import { ShieldCheck } from 'lucide-react';
import { api, getTenantFeatures } from '../lib/api';
import { useHasPerm } from '../lib/auth-store';

type PendingKyc = {
  id: string;
  userId: string;
  status: 'submitted';
  documents: Array<{ kind: string; r2Key: string }>;
  createdAt: string;
  userEmail: string;
  userFullName: string | null;
  /** The birth date the member typed, `YYYY-MM-DD`, or null. */
  userBirthDate: string | null;
};

export function KycReviewPage() {
  const canReview = useHasPerm('user.write');
  const pendingQ = useQuery({
    queryKey: ['kyc-pending'],
    queryFn: async () => (await api.get<PendingKyc[]>('/kyc/pending')).data,
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-h1 flex items-center gap-2">
          <ShieldCheck className="h-6 w-6 text-muted-foreground" /> KYC Review
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Members awaiting identity verification. Review their documents and approve or reject.
        </p>
      </div>

      {pendingQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !pendingQ.data?.length ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No pending KYC submissions. 🎉
          </CardContent>
        </Card>
      ) : (
        pendingQ.data.map((s) => <KycReviewCard key={s.id} submission={s} canReview={canReview} />)
      )}
    </div>
  );
}

function KycReviewCard({ submission, canReview }: { submission: PendingKyc; canReview: boolean }) {
  const qc = useQueryClient();
  const [rejectReason, setRejectReason] = useState('');
  // Only a tenant that asks for birth dates needs the "none entered" prompt.
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const asksBirthDate = (featuresQ.data?.birth_date_field ?? 'hidden') !== 'hidden';

  const docsQ = useQuery({
    queryKey: ['kyc-docs', submission.id],
    queryFn: async () =>
      (
        await api.get<{ documents: Array<{ kind: string; url: string }> }>(
          `/kyc/${submission.id}/documents`,
        )
      ).data.documents,
  });

  const review = useMutation({
    mutationFn: async (vars: { decision: 'verified' | 'rejected'; rejectionReason?: string }) =>
      (await api.post(`/kyc/${submission.id}/review`, vars)).data,
    meta: { successMessage: 'KYC reviewed.' },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['kyc-pending'] });
      qc.invalidateQueries({ queryKey: ['user', submission.userId] });
    },
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="text-base">
            {submission.userFullName || submission.userEmail}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {submission.userEmail} · submitted {new Date(submission.createdAt).toLocaleString()}
          </p>
          {!submission.userBirthDate && asksBirthDate && (
            <p className="text-sm">
              No birth date entered.
              <span className="block text-xs text-muted-foreground">
                The member can no longer add it while KYC is under review. If the ID shows it, add
                it on the member&apos;s page before verifying.
              </span>
            </p>
          )}
          {submission.userBirthDate && (
            <p className="text-sm">
              Birth date typed by the member:{' '}
              <span className="font-medium">
                {new Date(`${submission.userBirthDate}T00:00:00Z`).toLocaleDateString(undefined, {
                  timeZone: 'UTC',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </span>
              <span className="block text-xs text-muted-foreground">
                Check it against the ID. Verifying locks it on the member&apos;s profile; correct it
                on the member&apos;s page first if it is wrong.
              </span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">Pending review</Badge>
          <Link
            to={`/members/${submission.userId}`}
            className="text-xs font-medium text-primary hover:underline"
          >
            Open member ›
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          {docsQ.isLoading && <p className="text-xs text-muted-foreground">Loading documents…</p>}
          {docsQ.data?.map((d) => (
            <div key={d.kind} className="space-y-1">
              <div className="text-xs text-muted-foreground">{d.kind.replace(/_/g, ' ')}</div>
              <a href={d.url} target="_blank" rel="noreferrer">
                <img
                  src={d.url}
                  alt={d.kind}
                  className="aspect-[4/3] w-full rounded-md border object-cover"
                />
              </a>
            </div>
          ))}
        </div>

        {canReview && (
          <div className="flex flex-wrap items-center gap-2 border-t pt-3">
            <Button
              size="sm"
              disabled={review.isPending}
              onClick={() => review.mutate({ decision: 'verified' })}
            >
              Approve
            </Button>
            <Button
              size="sm"
              variant="destructive"
              disabled={review.isPending || rejectReason.trim() === ''}
              onClick={() =>
                review.mutate({ decision: 'rejected', rejectionReason: rejectReason.trim() })
              }
            >
              Reject
            </Button>
            <Input
              className="h-8 max-w-xs"
              placeholder="Rejection reason (required to reject)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
